import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { requireEnv } from '../config/requireEnv';

const auth0Domain = requireEnv('VITE_AUTH0_DOMAIN');
const auth0ClientId = requireEnv('VITE_AUTH0_CLIENT_ID');
const auth0Audience = requireEnv('VITE_AUTH0_AUDIENCE');

const oidcConfig: UserManagerSettings = {
  authority: `https://${auth0Domain}/`,
  client_id: auth0ClientId,
  redirect_uri: `${window.location.origin}/signin-callback`,
  post_logout_redirect_uri: `${window.location.origin}/signout-callback`,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-callback`,
  loadUserInfo: true,
  extraQueryParams: {
    audience: auth0Audience,
  },
  validateSubOnSilentRenew: true,
  monitorSession: false,
};

export const userManager = new UserManager(oidcConfig);

/**
 * Liest die org_id aus dem Auth0-Token und schreibt sie als organization-Parameter
 * in die extraQueryParams des UserManager. Dadurch wird die Organisation auch beim
 * automatischen Silent Renewal mitgeschickt, was Auth0 für Org-scoped Tokens erwartet.
 */
function syncOrgParam(user: User): void {
  const orgId = (user.profile as Record<string, unknown>).org_id as string | undefined;
  if (orgId) {
    // extraQueryParams ist readonly (darf nicht reassigned werden), das referenzierte
    // Objekt selbst ist aber mutierbar – Object.assign ist der korrekte Weg.
    Object.assign(userManager.settings.extraQueryParams, { organization: orgId });
  }
}

// Fall 1: Bereits gespeicherter User beim App-Start (z.B. nach Page-Reload)
userManager.getUser().then((user) => {
  if (user && !user.expired) syncOrgParam(user);
});

// Fall 2: Neuer Login-Callback oder Silent Renewal
userManager.events.addUserLoaded(syncOrgParam);

/**
 * Dekodiert den JWT-Payload eines Access Tokens (ohne Signaturprüfung).
 * Sicherheit: Das Backend validiert die Signatur kryptographisch.
 * Hier wird nur für UI-Entscheidungen dekodiert.
 */
function decodeTokenPayload(token: string): Record<string, unknown> {
  try {
    const base64Payload = token.split('.')[1];
    const jsonPayload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

export interface LoginOptions {
  invitation?: string;
  organization?: string;
  organizationName?: string;
}

export const oidcService = {
  async login(options: LoginOptions = {}): Promise<void> {
    const extraQueryParams: Record<string, string> = {
      audience: auth0Audience,
    };
    if (options.invitation) extraQueryParams.invitation = options.invitation;
    if (options.organization) extraQueryParams.organization = options.organization;
    if (options.organizationName) extraQueryParams.organization_name = options.organizationName;

    // Bei Einladungslinks direkt Signup-Screen zeigen statt Login (kein "Reset password" nötig)
    if (options.invitation) extraQueryParams.screen_hint = 'signup';

    await userManager.signinRedirect({ extraQueryParams });
  },

  async logout(): Promise<void> {
    const returnTo = encodeURIComponent(`${window.location.origin}/signout-callback`);
    window.location.href = `https://${auth0Domain}/v2/logout?client_id=${auth0ClientId}&returnTo=${returnTo}`;
    return new Promise(() => {});
  },

  async getUser() {
    try {
      return await userManager.getUser();
    } catch {
      return null;
    }
  },

  async getAccessToken(): Promise<string | null> {
    try {
      const user = await this.getUser();
      return user?.access_token || null;
    } catch {
      return null;
    }
  },

  /** Extrahiert das ``permissions``-Array aus dem Access Token (Auth0 RBAC). */
  async getPermissions(): Promise<string[]> {
    try {
      const token = await this.getAccessToken();
      if (!token) return [];
      const payload = decodeTokenPayload(token);
      const permissions = payload.permissions;
      return Array.isArray(permissions) ? (permissions as string[]) : [];
    } catch {
      return [];
    }
  },

  async hasPermission(permission: string): Promise<boolean> {
    const permissions = await this.getPermissions();
    return permissions.includes(permission);
  },

  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getUser();
      return user !== null && !user.expired;
    } catch {
      return false;
    }
  },
};

export default oidcService;
